import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { stemPollDelay, stemTaskIsTerminal } from "./stemTask";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function useStemTask(analysisId, getAuthHeaders, preferredTaskId = "") {
  const headers = useRef(getAuthHeaders);
  headers.current = getAuthHeaders;
  const [snapshot, setSnapshot] = useState({ analysisId, task: null, error: "", retrying: false });
  const [revision, setRevision] = useState(0);
  const active = useRef(null);

  useEffect(() => {
    if (!analysisId) return undefined;
    const controller = new AbortController();
    active.current = controller;
    let timer;
    let attempt = 0;
    let taskId = revision ? "" : preferredTaskId;
    setSnapshot({ analysisId, task: null, error: "", retrying: false });
    const poll = async () => {
      try {
        const path = taskId ? `task/${encodeURIComponent(taskId)}` : `analysis/${encodeURIComponent(analysisId)}/latest`;
        const { data } = await axios.get(`${API}/stems/${path}`, {
          headers: headers.current(), signal: controller.signal,
        });
        if (controller.signal.aborted) return;
        if (data.analysis_id && data.analysis_id !== analysisId) throw new Error("La tarea no corresponde a este analisis");
        taskId = data.id || "";
        setSnapshot({ analysisId, task: data, error: "", retrying: false });
        if (stemTaskIsTerminal(data)) return;
      } catch (failure) {
        if (controller.signal.aborted) return;
        // A legacy analysis may not have a task yet; latest safely ensures it server-side.
        if (failure.response?.status === 404) taskId = "";
        else {
          setSnapshot(prev => ({ ...prev, analysisId, error: failure.message || "No se pudo consultar el estado de las pistas" }));
          if ([401, 403].includes(failure.response?.status)) return;
        }
      }
      if (!controller.signal.aborted) timer = setTimeout(poll, stemPollDelay(attempt++));
    };
    poll();
    return () => { controller.abort(); clearTimeout(timer); };
  }, [analysisId, preferredTaskId, revision]);

  const retry = useCallback(async () => {
    const controller = active.current;
    if (!controller || controller.signal.aborted || controller.retrying) return;
    controller.retrying = true;
    setSnapshot(prev => ({ ...prev, retrying: true, error: "" }));
    try {
      await axios.post(`${API}/stems/separate/${encodeURIComponent(analysisId)}`, {}, {
        headers: headers.current(), signal: controller.signal,
      });
      if (!controller.signal.aborted) setRevision(value => value + 1);
    } catch (failure) {
      if (!controller.signal.aborted) setSnapshot(prev => ({ ...prev, retrying: false,
        error: typeof failure.response?.data?.detail === "string" ? failure.response.data.detail : "No se pudo reintentar la separacion" }));
    } finally { controller.retrying = false; }
  }, [analysisId]);

  const current = snapshot.analysisId === analysisId ? snapshot : { task: null, error: "", retrying: false };
  return { ...current, retry };
}
